import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Skeleton,
  Alert,
  useMediaQuery,
  useTheme,
  Fade,
  Tooltip,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
  GetApp as ExportIcon,
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { motion, AnimatePresence } from 'framer-motion';
import { getSavedPayrolls, prefetchPayrollById, deletePayroll, revertPayrollToDraft, requestEditOtp, verifyOtp, changeDisplay, searchPayrollsCompleted } from '../../api/payroll';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import axios from 'axios';
import { getStoredUser } from '../../api/client';
import OtpInput from 'react-otp-input';
import { exportToPrint, exportToPDF } from '../../utils/exportUtils';

axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;

const SavePayrolls = () => {
  let navigate = useNavigate();

  const [selectedRecords, setSelectedRecords] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [user, setUser] = useState();
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [revertingId, setRevertingId] = useState(null);
  const [showAllowances] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const loadControllerRef = useRef(null);

  const [otpDialog, setOtpDialog] = useState({
    open: false,
    payrollId: null,
    otp: '',
    requestedBy: '',
    loading: false,
    cooldownTime: 0,
    lastOtpRequest: null,
    step: 1 
  });

  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleEditClick = (payroll) => {
    const storedCooldown = getStoredCooldown();

    setOtpDialog({
      open: true,
      payrollId: payroll._id,
      otp: '',
      requestedBy: '',
      loading: false,
      cooldownTime: storedCooldown.payrollId === payroll.id ? storedCooldown.cooldownTime : 0,
      lastOtpRequest: storedCooldown.payrollId === payroll.id ? storedCooldown.lastOtpRequest : null,
      step: 1
    });
    setEditingPayroll(payroll);
  };

  const handleRevertToDraft = async () => {
    if (!editingPayroll) return;

    const payrollId = editingPayroll._id || editingPayroll.id;
    if (!payrollId) {
      enqueueSnackbar('Invalid payroll. Missing id.', { variant: 'error' });
      return;
    }

    try {
      setRevertingId(payrollId);

      const result = await revertPayrollToDraft(payrollId);

      if (result.status === 'success') {
        enqueueSnackbar('Payroll reverted to draft successfully. You can now edit this payroll.', { variant: 'success' });
        // Close the dialog and refresh the data
        handleOtpClose();
        await load(false, isSearching ? searchTerm : null);
      } else {
        enqueueSnackbar(result.message || 'Failed to revert payroll to draft', { variant: 'error' });
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || 'Failed to revert payroll to draft';
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setRevertingId(null);
      setEditingPayroll(null);
    }
  };

  const getStoredCooldown = () => {
    const stored = localStorage.getItem('otp_cooldown');
    if (stored) {
      const { lastRequest, payrollId } = JSON.parse(stored);
      const timeElapsed = Math.floor((Date.now() - lastRequest) / 1000);
      const remainingTime = Math.max(0, 60 - timeElapsed);
      return { lastOtpRequest: lastRequest, cooldownTime: remainingTime, payrollId: payrollId };
    }
    return { lastOtpRequest: null, cooldownTime: 0, payrollId: null };
  };

  const setStoredCooldown = (payrollId) => {
    localStorage.setItem('otp_cooldown', JSON.stringify({ lastRequest: Date.now(), payrollId }));
  };

  const clearStoredCooldown = () => localStorage.removeItem('otp_cooldown');

  // OTP Dialog handlers
  const handleOtpClose = () => {
    clearStoredCooldown();
    setOtpDialog({
      open: false,
      payrollId: null,
      otp: '',
      requestedBy: '',
      loading: false,
      cooldownTime: 0,
      lastOtpRequest: null,
      step: 1
    });
    setEditingPayroll(null);
  };

  const handleOtpChange = (value) => {
    setOtpDialog(prev => ({ ...prev, otp: value }));
  };

  const handleRequestedByChange = (event) => {
    setOtpDialog(prev => ({ ...prev, requestedBy: event.target.value }));
  };

  const handleProceedToOtp = () => {
    if (!otpDialog.requestedBy.trim()) {
      enqueueSnackbar('Please enter who requested this edit', { variant: 'warning' });
      return;
    }
    setOtpDialog(prev => ({ ...prev, step: 2 }));
  };

  const handleOtpSubmit = async () => {
    if (otpDialog.otp.length !== 6) {
      enqueueSnackbar('Please enter the complete 6-digit OTP', { variant: 'warning' });
      return;
    }

    if (!otpDialog.requestedBy.trim()) {
      enqueueSnackbar('Please enter who requested this edit', { variant: 'warning' });
      return;
    }

    setOtpDialog(prev => ({ ...prev, loading: true }));

    try {
      // Verify OTP with backend
      const response = await verifyOtp(otpDialog.otp);

      if (response.status === 'success') {
        clearStoredCooldown();
        enqueueSnackbar('OTP verified successfully', { variant: 'success' });
        setOtpDialog(prev => ({ ...prev, loading: false, cooldownTime: 0, lastOtpRequest: null, step: 3 }));
      } else {
        enqueueSnackbar('Invalid OTP. Please try again.', { variant: 'error' });
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      enqueueSnackbar('Failed to verify OTP. Please try again.', { variant: 'error' });
    } finally {
      setOtpDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const requestOtp = async () => {
    if (!otpDialog.payrollId || otpDialog.cooldownTime > 0) return;

    try {
      const response = await requestEditOtp(otpDialog.payrollId);

      if (response.status === 'success') {
        setStoredCooldown(otpDialog.payrollId);
        setOtpDialog(prev => ({
          ...prev,
          cooldownTime: 60, // 1 minute cooldown
          lastOtpRequest: Date.now()
        }));
        enqueueSnackbar('OTP sent to your registered email', { variant: 'info' });
      } else {
        enqueueSnackbar('Failed to send OTP. Please try again.', { variant: 'error' });
      }
    } catch (error) {
      console.error('OTP request error:', error);
      enqueueSnackbar('Failed to send OTP. Please try again.', { variant: 'error' });
    }
  };

  const load = async (showLoading = true, searchQuery = null, signal = null) => {
    let cancelled = false;
    try {
      if (showLoading) setLoading(true);
      setUser(getStoredUser());
      const res = searchQuery !== null
        ? await searchPayrollsCompleted(page, rowsPerPage, searchQuery, signal)
        : await getSavedPayrolls(page, rowsPerPage, selectedStore, signal);
      setRecords(res.data || []);
      setTotal(res.totalItems || 0);
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') {
        cancelled = true;
        return;
      }
      setError('Failed to load payrolls');
      setRecords([]);
    } finally {
      if (showLoading && !cancelled) setLoading(false);
    }
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    loadControllerRef.current?.abort();
    const controller = new AbortController();
    loadControllerRef.current = controller;
    await load(false, isSearching ? searchTerm : null, controller.signal);
    setRefreshing(false);
  };

  useEffect(() => {
    loadControllerRef.current?.abort();
    const controller = new AbortController();
    loadControllerRef.current = controller;
    load(true, isSearching ? searchTerm : null, controller.signal);
    return () => {
      controller.abort();
      loadControllerRef.current = null;
    };
  }, [page, rowsPerPage, selectedStore]);

  // Countdown timer effect for OTP cooldown
  useEffect(() => {
    let interval;
    if (otpDialog.cooldownTime > 0) {
      interval = setInterval(() => {
        setOtpDialog(prev => {
          const newCooldownTime = Math.max(0, prev.cooldownTime - 1);
          if (newCooldownTime === 0) {
            clearStoredCooldown();
          }
          return { ...prev, cooldownTime: newCooldownTime };
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpDialog.cooldownTime]);

  // Initialize cooldown on component mount
  useEffect(() => {
    const storedCooldown = getStoredCooldown();
    if (storedCooldown.cooldownTime > 0) {
      setOtpDialog(prev => ({
        ...prev,
        cooldownTime: storedCooldown.cooldownTime,
        lastOtpRequest: storedCooldown.lastOtpRequest
      }));
    }
  }, []);

  // Auto-request OTP when moving to step 2
  useEffect(() => {
    if (otpDialog.open && otpDialog.payrollId && otpDialog.step === 2) {
      requestOtp();
    }
  }, [otpDialog.open, otpDialog.payrollId, otpDialog.step]);

  const uniqueAllowances = useMemo(() => {
    const allowanceMap = new Map();
    let hasPersonnel = false;
    records.forEach(record => {
      record.informations?.forEach(info => {
        const payrollAllowances = info?.payroll_allowances || [];
        payrollAllowances.forEach(allowance => {
          if (allowance?.allowance_type) {
            const src = allowance.source || '';
            if (src === 'personnel' || allowance.user_id) {
              hasPersonnel = true;
              return;
            }
            allowanceMap.set(allowance.allowance_type, {
              _id: allowance._id || allowance.allowance_type,
              name: allowance.allowance_type,
              source: src || 'unknown'
            });
          }
        });
      });
    });
    const rest = Array.from(allowanceMap.values());
    if (hasPersonnel) {
      return [
        {
          _id: '__personnel_consolidated__',
          name: 'Personnel allowances',
          source: 'personnel',
          isConsolidatedPersonnel: true
        },
        ...rest
      ];
    }
    return rest;
  }, [records]);

  // FIX: This was the source of the Unexpected Token error (missing closing braces)
  const filteredRecords = useMemo(() => {
    return records.filter(record => {
      const matchesSearch = !searchTerm || record.store?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesGroup = !selectedGroup || record.group === selectedGroup;
      const matchesStore = !selectedStore || record.store === selectedStore;
      const matchesStatus = !saveStatus || record.status === saveStatus;

      return matchesSearch && matchesGroup && matchesStore && matchesStatus;
    });
  }, [records, searchTerm, selectedGroup, selectedStore, saveStatus]);

  // Prepare data for export
  const dataToExport = useMemo(() => {
    if (!records || records.length === 0) return [];
    
    return records.map(item => {
      const {
        _id,
        createdAt,
        updatedAt,
        __v,
        informations,
        total_amount,
        total_employees,
        ...rest
      } = item;

      const sss = informations?.reduce((acc, i) => acc + (i.sss_contribution || 0), 0) || 0;
      const hdmf = informations?.reduce((acc, i) => acc + (i.hdmf_contribution || 0), 0) || 0;
      const nhip = informations?.reduce((acc, i) => acc + (i.nhip_contribution || 0), 0) || 0;
      const totalBasicPay = informations?.reduce((sum, info) => sum + (parseFloat(info.basic_pay) || 0), 0) || 0;

      return {
        'Payroll ID': _id,
        'Store': item.store || '',
        'Company': item.company || 'N/A',
        'Generated By': item.generated_by_name || '',
        'Approved By': item.approved_by || '',
        'Period From': item.payroll_from ? dayjs(item.payroll_from).format('YYYY-MM-DD') : '',
        'Period To': item.payroll_to ? dayjs(item.payroll_to).format('YYYY-MM-DD') : '',
        'Date Generated': item.date_generated ? dayjs(item.date_generated).format('YYYY-MM-DD') : '',
        'Employee Count': informations?.length || 0,
        'Total Basic Pay': totalBasicPay.toLocaleString("en-PH", {
          style: "currency",
          currency: "PHP",
          minimumFractionDigits: 2,
        }),
        'SSS Contribution': sss.toLocaleString("en-PH", { minimumFractionDigits: 2 }),
        'HDMF Contribution': hdmf.toLocaleString("en-PH", { minimumFractionDigits: 2 }),
        'NHIP Contribution': nhip.toLocaleString("en-PH", { minimumFractionDigits: 2 }),
        'Total Contributions': (sss + hdmf + nhip).toLocaleString("en-PH", { minimumFractionDigits: 2 }),
        'Status': item.status || 'completed'
      };
    });
  }, [records]);

  const handleExportCSV = () => {
    if (!records.length) return;
    const csvHeaders = Object.keys(dataToExport[0]);
    const csvRows = [csvHeaders.join(','), ...dataToExport.map(row => csvHeaders.map(h => `"${row[h]}"`).join(','))];
    const blob = new Blob(["\uFEFF" + csvRows.join('\n')], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'payrolls.csv';
    link.click();
  };

  const handleDisplayChange = async (record) => {
    const res = await changeDisplay(record._id);
    if(res.status === "success") load();
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setIsSearching(true);
    setPage(1);
    loadControllerRef.current?.abort();
    const controller = new AbortController();
    loadControllerRef.current = controller;
    await load(true, searchTerm, controller.signal);
  };

  const handleClearSearch = async () => {
    setSearchTerm('');
    setIsSearching(false);
    setPage(1);
    loadControllerRef.current?.abort();
    const controller = new AbortController();
    loadControllerRef.current = controller;
    await load(true, null, controller.signal);
  };

  return (
    <Fade in timeout={600}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon sx={{ color: '#12C1DC' }} /> Saved Payrolls
          </Typography>
          <IconButton onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Box>

        <Paper sx={{ mb: 3, p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField 
                size="small" 
                placeholder="Search..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
            />
            <Button variant="contained" onClick={handleSearch} sx={{ bgcolor: '#12C1DC' }}>Search</Button>
            {isSearching && <Button onClick={handleClearSearch}>Clear</Button>}
            <Tooltip title="Export CSV"><IconButton onClick={handleExportCSV}><ExportIcon /></IconButton></Tooltip>
            <Tooltip title="Print"><IconButton onClick={() => exportToPrint(dataToExport, 'Report')}><PrintIcon /></IconButton></Tooltip>
          </Box>
        </Paper>

        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f9fafb' }}>Actions</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f9fafb' }}>Payroll ID</TableCell>
                  {!isMobile && <TableCell sx={{ fontWeight: 600, bgcolor: '#f9fafb' }}>Company</TableCell>}
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f9fafb' }}>Store</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: '#f9fafb' }}>Generated By</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: '#f9fafb' }}>Approved By</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: '#f9fafb' }}>Cut Off</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: '#f9fafb' }}>Employees</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#f9fafb' }}>Total Basic Pay</TableCell>
                  {uniqueAllowances.map((allowance) => (
                    <TableCell key={allowance._id} align="center" sx={{ minWidth: '120px' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                          {allowance.name}
                        </Typography>
                        <Chip
                          label={allowance.source === 'personnel' ? 'P' : allowance.source === 'store' ? 'S' : 'G'}
                          size="small"
                          sx={{
                            fontSize: '0.6rem',
                            height: '16px',
                            bgcolor: allowance.source === 'personnel' ? '#e3f2fd' : allowance.source === 'store' ? '#e8f5e9' : '#fff3e0',
                            color: allowance.source === 'personnel' ? '#1976d2' : allowance.source === 'store' ? '#2e7d32' : '#e65100'
                          }}
                        />
                      </Box>
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ 
                    fontWeight: 600, 
                    bgcolor: '#f9fafb',
                    color: '#374151',
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Contributions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: Math.min(rowsPerPage, 5) }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      <TableCell colSpan={10 + uniqueAllowances.length} sx={{ py: 2 }}>
                        <Skeleton variant="text" width="100%" height={24} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                <AnimatePresence>
                  {records.map((record, index) => {
                    const sss = record.informations?.reduce((acc, i) => acc + (i.sss_contribution || 0), 0) || 0;
                    const hdmf = record.informations?.reduce((acc, i) => acc + (i.hdmf_contribution || 0), 0) || 0;
                    const nhip = record.informations?.reduce((acc, i) => acc + (i.nhip_contribution || 0), 0) || 0;

                    const isEvenRow = index % 2 === 0;
                    return (
                      <TableRow
                        key={record.id}
                        sx={{
                          backgroundColor: isEvenRow ? '#ffffff' : '#f8f9fa',
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': {
                            backgroundColor: isEvenRow ? '#f0f0f0 !important' : '#e9ecef !important',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                          },
                          '& > .MuiTableCell-root': {
                            borderRight: '1px solid #e0e0e0',
                            borderBottom: '1px solid #e0e0e0'
                          },
                          '& > .MuiTableCell-root:last-child': {
                            borderRight: 'none'
                          },
                          transition: 'all 0.2s ease-in-out',
                          cursor: 'pointer'
                        }}
                      >
                        <TableCell component="th" scope="row" id={`enhanced-table-checkbox-${record.id}`}>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Tooltip title={record.display === 1 ? "Remove payslip" : "Display payslip"}>
                              <IconButton
                                size="small"
                                disabled={revertingId === (record._id || record.id)}
                                onMouseEnter={() => prefetchPayrollById(record._id || record.id)}
                                onClick={() => handleDisplayChange(record)}
                                sx={{
                                  color: '#12C1DC',
                                  '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' }
                                }}
                              >
                                {revertingId === (record._id || record.id) ? (
                                  <CircularProgress size={16} sx={{ color: '#12C1DC' }} />
                                ) : record.display === 1 ? (
                                  <RemoveIcon fontSize="small" />
                                ) : (
                                  <AddIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={record.display === 1 ? "Remove display payslip first" : "Edit payroll"}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={revertingId === (record._id || record.id) || record.display === 1}
                                  onMouseEnter={() => prefetchPayrollById(record._id || record.id)}
                                  onClick={() => handleEditClick(record)}
                                  sx={{
                                    color: '#12C1DC',
                                    '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' }
                                  }}
                                >
                                  {revertingId === (record._id || record.id) ? (
                                    <CircularProgress size={16} sx={{ color: '#12C1DC' }} />
                                  ) : (
                                    <EditIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="View payroll data">
                              <IconButton
                                size="small"
                                onMouseEnter={() => prefetchPayrollById(record._id || record.id)}
                                onClick={() => navigate(`../payroll/${record._id}/view`)}
                                sx={{
                                  color: '#6366f1',
                                  '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.1)' }
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete payroll">
                              <IconButton
                                size="small"
                                disabled={deletingId === record.id}
                                onClick={async () => {
                                  const confirmed = await Swal.fire({
                                    title: 'Delete Payroll',
                                    text: 'Are you sure you want to delete this payroll? This action cannot be undone.',
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonText: 'Delete',
                                    cancelButtonText: 'Cancel'
                                  });
                                  if (!confirmed.isConfirmed) return;
                                  try {
                                    setDeletingId(record.id);
                                    await deletePayroll(record.id);
                                    setRecords((prev) => prev.filter(x => x.id !== record.id));
                                    enqueueSnackbar('Payroll deleted successfully', { variant: 'success' });
                                  } catch (err) {
                                    console.error(err);
                                    enqueueSnackbar('Failed to delete payroll', { variant: 'error' });
                                  } finally {
                                    setDeletingId(null);
                                  }
                                }}
                                sx={{
                                  color: '#ef4444',
                                  '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' }
                                }}
                              >
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Payroll Billing">
                              <IconButton
                                onClick={() => navigate(`../payroll/${record._id}/billing`)}
                                size="small"
                                sx={{
                                  color: '#10b981',
                                  '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.1)' }
                                }}
                              >
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell component="th" scope="row" id={`enhanced-table-checkbox-${record.id}`} align="center">
                          {record.id}
                        </TableCell>
                        {!isMobile && <TableCell align="center">N/A</TableCell>}
                        <TableCell align="center">{record.store}</TableCell>
                        <TableCell align="center">{record.generated_by_name}</TableCell>
                        <TableCell align="center">{record.approved_by}</TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600}>
                            {dayjs(record.payroll_from).format("MMM DD")} - {dayjs(record.payroll_to).format("MMM DD, YYYY")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Created: {dayjs(record.date_generated).format("MMM DD, YYYY")}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{record.informations?.length || 0}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#059669' }}>
                              {record.source === "System Generated"
                                ? "Please Set Rates"
                                : (
                                  record?.informations?.reduce((sum, info) => {
                                    return sum + (info.basic_pay || 0);
                                  }, 0)
                                ).toLocaleString("en-PH", {
                                  style: "currency",
                                  currency: "PHP",
                                  minimumFractionDigits: 2,
                                })}
                            </Typography>
                          </Box>
                        </TableCell>
                        {showAllowances && uniqueAllowances.map((allowance) => {
                          // Calculate total for this allowance across all employees in this payroll
                          // Use payroll_allowances from PayrollInfo
                          const allowanceTotal = (record?.informations || []).reduce((sum, info) => {
                            const hw = info.hours_work || 0;
                            // First try payroll_allowances
                            const payrollAllowances = info?.payroll_allowances || [];
                            if (allowance.isConsolidatedPersonnel) {
                              if (payrollAllowances.length > 0) {
                                const personnelSum = payrollAllowances
                                  .filter(a => a.source === 'personnel' || a.user_id)
                                  .reduce((s, a) => s + (Number(a.amount) || 0), 0);
                                return sum + personnelSum * hw;
                              }
                              if (info.allowances && Array.isArray(info.allowances)) {
                                const personnelSum = info.allowances
                                  .filter(a => a.source === 'personnel' || a.user_id)
                                  .reduce((s, a) => s + (Number(a.amount) || 0), 0);
                                return sum + personnelSum * hw;
                              }
                              return sum;
                            }
                            if (payrollAllowances.length > 0) {
                              const empAllowance = payrollAllowances.find(a => 
                                a?.allowance_type === allowance.name || 
                                a?._id?.toString() === allowance._id?.toString()
                              );
                              return sum + (empAllowance?.amount || 0) * hw;
                            }
                            // Fallback to info.allowances for backward compatibility
                            if (info.allowances && Array.isArray(info.allowances)) {
                              const empAllowance = info.allowances.find(a => 
                                a?._id?.toString() === allowance._id?.toString() ||
                                a?.name === allowance.name
                              );
                              return sum + (empAllowance?.amount || 0) * hw;
                            }
                            return sum;
                          }, 0);
                          
                          // Get source distribution (count by source)
                          const sourceCounts = { personnel: 0, store: 0, group: 0 };
                          (record?.informations || []).forEach(info => {
                            // First try payroll_allowances
                            const payrollAllowances = info?.payroll_allowances || [];
                            if (allowance.isConsolidatedPersonnel) {
                              if (payrollAllowances.some(a => a.source === 'personnel' || a.user_id)) {
                                sourceCounts.personnel += 1;
                              }
                              return;
                            }
                            if (payrollAllowances.length > 0) {
                              const empAllowance = payrollAllowances.find(a => 
                                a?.allowance_type === allowance.name || 
                                a?._id?.toString() === allowance._id?.toString()
                              );
                              if (empAllowance && empAllowance.source) {
                                sourceCounts[empAllowance.source] = (sourceCounts[empAllowance.source] || 0) + 1;
                              }
                            }
                            // Fallback to info.allowances
                            else if (info.allowances && Array.isArray(info.allowances)) {
                              const empAllowance = info.allowances.find(a => 
                                a?._id?.toString() === allowance._id?.toString() ||
                                a?.name === allowance.name
                              );
                              if (empAllowance && empAllowance.source) {
                                sourceCounts[empAllowance.source] = (sourceCounts[empAllowance.source] || 0) + 1;
                              }
                            }
                          });
                          
                          // Determine primary source (most common)
                          const primarySource = Object.entries(sourceCounts).reduce((a, b) => sourceCounts[a[0]] > sourceCounts[b[0]] ? a : b, ['unknown', 0])[0];
                          
                          return (
                            <TableCell key={allowance._id} align="right">
                              {allowanceTotal > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                                  <Typography variant="body2" fontWeight={500}>
                                    ₱{allowanceTotal.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                                  </Typography>
                                  <Chip
                                    label={primarySource === 'personnel' ? 'P' : primarySource === 'store' ? 'S' : 'G'}
                                    size="small"
                                    sx={{
                                      fontSize: '0.6rem',
                                      height: '16px',
                                      bgcolor: primarySource === 'personnel' ? '#e3f2fd' : primarySource === 'store' ? '#e8f5e9' : '#fff3e0',
                                      color: primarySource === 'personnel' ? '#1976d2' : primarySource === 'store' ? '#2e7d32' : '#e65100'
                                    }}
                                  />
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell>
                          <Box sx={{ fontSize: '0.75rem' }}>
                            <Typography variant="caption" display="block">
                              <strong>SSS:</strong> ₱{sss.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                            </Typography>
                            <Typography variant="caption" display="block">
                              <strong>HDMF:</strong> ₱{hdmf.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                            </Typography>
                            <Typography variant="caption" display="block">
                              <strong>NHIP:</strong> ₱{nhip.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </AnimatePresence>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderTop: '1px solid #e5e7eb',
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
              Showing {(page - 1) * rowsPerPage + 1} to{" "}
              {Math.min(page * rowsPerPage, total)} of {total} entries
              {isSearching && ` (search results)`}
            </Typography>
            <TablePagination
              component="div"
              count={total}
              page={page - 1}
              onPageChange={(e, newPage) => handleChangePage(newPage + 1)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                const newRowsPerPage = parseInt(e.target.value, 10);
                setRowsPerPage(newRowsPerPage);
                setPage(1);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage=""
              sx={{
                '& .MuiTablePagination-toolbar': {
                  minHeight: 'auto',
                  paddingLeft: 0,
                  paddingRight: 0
                },
                '& .MuiTablePagination-selectLabel': {
                  display: 'none'
                },
                '& .MuiTablePagination-select': {
                  display: 'none'
                },
                '& .MuiTablePagination-actions button': {
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(18, 193, 220, 0.1)'
                  }
                }
              }}
            />
          </Box>
        </Paper>

        {/* OTP Verification Dialog */}
        <Dialog
          open={otpDialog.open}
          onClose={handleOtpClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }
          }}
        >
          <DialogTitle sx={{
            textAlign: 'center',
            pb: 2,
            background: 'linear-gradient(135deg, #12C1DC 0%, #0fb8d0 100%)',
            color: 'white'
          }}>
            <Typography variant="h6" fontWeight="bold">
              {otpDialog.step === 1 ? 'Proceed to Edit Payroll' :
               otpDialog.step === 2 ? 'Verify OTP to Edit Payroll' :
               'Confirm Edit Payroll'}
            </Typography>
            {otpDialog.step === 2 && otpDialog.cooldownTime > 0 && (
              <Typography variant="caption" sx={{ mt: 1, opacity: 0.8, display: 'block' }}>
                Next OTP available in {otpDialog.cooldownTime}s
              </Typography>
            )}

            {/* Step Indicator */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: otpDialog.step >= 1 ? 'white' : 'rgba(255,255,255,0.3)'
              }} />
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: otpDialog.step >= 2 ? 'white' : 'rgba(255,255,255,0.3)'
              }} />
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: otpDialog.step >= 3 ? 'white' : 'rgba(255,255,255,0.3)'
              }} />
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {/* Step 1: Requested By */}
            {otpDialog.step === 1 && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 3, mt:2, color: '#374151', fontWeight: 600 }}>
                  Proceed to editing this payroll?
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Requested By"
                    variant="outlined"
                    value={otpDialog.requestedBy}
                    onChange={handleRequestedByChange}
                    placeholder="Enter who requested this edit"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': { borderColor: '#12C1DC' },
                        '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                      }
                    }}
                  />
                </Box>

                {editingPayroll && (
                  <Box sx={{
                    bgcolor: '#f8f9fa',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #e5e7eb',
                    textAlign: 'left'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                      Payroll Details:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Store:</strong> {editingPayroll.store}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Period:</strong> {dayjs(editingPayroll.payroll_from).format("MMM DD")} - {dayjs(editingPayroll.payroll_to).format("MMM DD, YYYY")}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Employees:</strong> {editingPayroll.informations?.length || 0}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            {/* Step 2: OTP Verification */}
            {otpDialog.step === 2 && (
              <>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body1" sx={{ mb: 2, color: '#6b7280' }}>
                    Enter the 6-digit code sent to your email
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%'
                  }}>
                    <OtpInput
                      value={otpDialog.otp}
                      onChange={handleOtpChange}
                      numInputs={6}
                      separator={<span style={{ width: '8px' }}></span>}
                      inputType="tel"
                      shouldAutoFocus={true}
                      renderInput={(props, index) => (
                        <input
                          {...props}
                          key={index}
                          style={{
                            width: '2.5rem',
                            height: '2.5rem',
                            margin: '0 0.25rem',
                            fontSize: '1.25rem',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            border: otpDialog.otp.length === 6 ? '2px solid #10b981' : '2px solid #d1d5db',
                            textAlign: 'center',
                            outline: 'none',
                            transition: 'all 0.2s ease-in-out',
                            backgroundColor: otpDialog.otp.length === 6 ? '#f0fdf4' : '#ffffff'
                          }}
                          onFocus={(e) => {
                            e.target.style.border = '2px solid #12C1DC';
                            e.target.style.outline = 'none';
                          }}
                          onBlur={(e) => {
                            e.target.style.border = otpDialog.otp.length === 6 ? '2px solid #10b981' : '2px solid #d1d5db';
                          }}
                        />
                      )}
                    />
                  </Box>
                  {otpDialog.otp.length === 6 && (
                    <Typography variant="caption" sx={{ mt: 1, color: '#10b981', fontWeight: 500 }}>
                      ✓ All digits entered
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="text"
                    onClick={requestOtp}
                    disabled={otpDialog.loading || otpDialog.cooldownTime > 0}
                    sx={{
                      color: otpDialog.cooldownTime > 0 ? '#9ca3af' : '#12C1DC',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: otpDialog.cooldownTime > 0 ? 'transparent' : 'rgba(18, 193, 220, 0.1)'
                      },
                      '&:disabled': {
                        color: '#9ca3af',
                        cursor: 'not-allowed'
                      }
                    }}
                  >
                    {otpDialog.cooldownTime > 0
                      ? `Resend OTP in ${otpDialog.cooldownTime}s`
                      : "Didn't receive OTP? Resend"
                    }
                  </Button>
                </Box>
              </>
            )}

            {/* Step 3: Edit Confirmation */}
            {otpDialog.step === 3 && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 3, mt: 2, color: '#374151', fontWeight: 600 }}>
                  Edit Saved Payroll
                </Typography>

                <Typography variant="body1" sx={{ mb: 3, color: '#6b7280' }}>
                  Saved payrolls cannot be edited directly. To make changes, you need to revert this payroll back to draft status.
                </Typography>

                {editingPayroll && (
                  <Box sx={{
                    bgcolor: '#f8f9fa',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #e5e7eb',
                    textAlign: 'left',
                    mb: 3
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                      Payroll Details:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Store:</strong> {editingPayroll.store}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Period:</strong> {dayjs(editingPayroll.payroll_from).format("MMM DD")} - {dayjs(editingPayroll.payroll_to).format("MMM DD, YYYY")}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Employees:</strong> {editingPayroll.informations?.length || 0}
                    </Typography>
                  </Box>
                )}

                <Typography variant="body1" sx={{ color: '#374151', fontWeight: 500 }}>
                  Are you sure you want to revert this payroll to draft status and continue with editing?
                </Typography>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0, gap: 2, justifyContent: 'center' }}>
            <Button
              onClick={
                otpDialog.step === 2 ? () => setOtpDialog(prev => ({ ...prev, step: 1 })) :
                otpDialog.step === 3 ? () => setOtpDialog(prev => ({ ...prev, step: 2 })) :
                handleOtpClose
              }
              variant="outlined"
              disabled={otpDialog.loading || (otpDialog.step === 3 && revertingId === (editingPayroll?._id || editingPayroll?.id))}
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f9fafb'
                }
              }}
            >
              {otpDialog.step === 1 ? 'Cancel' :
               otpDialog.step === 2 ? 'Back' :
               'Back'}
            </Button>

            <Button
              onClick={
                otpDialog.step === 1 ? handleProceedToOtp :
                otpDialog.step === 2 ? handleOtpSubmit :
                handleRevertToDraft
              }
              variant="contained"
              disabled={
                otpDialog.step === 1 ? !otpDialog.requestedBy.trim() :
                otpDialog.step === 2 ? otpDialog.loading || otpDialog.otp.length !== 6 :
                revertingId === editingPayroll?.id
              }
              sx={{
                bgcolor: '#12C1DC',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#0fb8d0' },
                '&:disabled': {
                  bgcolor: '#e5e7eb',
                  color: '#9ca3af'
                }
              }}
            >
              {otpDialog.step === 1 ? 'Proceed' :
               otpDialog.step === 2 ? (
                 otpDialog.loading ? (
                   <>
                     <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                     Verifying...
                   </>
                 ) : 'Verify'
               ) :
               revertingId === (editingPayroll?._id || editingPayroll?.id) ? (
                 <>
                   <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                   Reverting...
                 </>
               ) : (
                 'Revert to Draft & Edit'
               )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default SavePayrolls;
